import React, { useState, useEffect } from 'react';
import { MapPin, Phone, Mail, Send, ArrowRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { companyInfo } from '../data/mock';
import { toast } from 'sonner';
import axios from 'axios';

const ContactPage = () => {
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState({ name: '', phone: '', email: '', message: '', project: '' });
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  // Scroll reveal animation
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.scroll-reveal, .scroll-reveal-left, .scroll-reveal-right, .scroll-reveal-scale').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.phone) {
      toast.error(t('contact.form.error'));
      return;
    }
    setIsSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/contact`, formData);
      toast.success(t('contact.form.success'));
      setFormData({ name: '', phone: '', email: '', message: '', project: '' });
    } catch (error) {
      toast.error('Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!newsletterEmail) {
      toast.error('Please enter your email');
      return;
    }
    setIsSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/newsletter/subscribe`, { email: newsletterEmail });
      toast.success('Successfully subscribed to newsletter!');
      setNewsletterEmail('');
    } catch (error) {
      toast.error('Failed to subscribe. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const mapUrl = `https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3189.123456789!2d${companyInfo.mapCoordinates.lng}!3d${companyInfo.mapCoordinates.lat}!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzXCsDMzJzIwLjkiTiA0NcKwMjUnNTguNCJF!5e0!3m2!1sen!2s!4v1234567890`;

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Hero Section */}
      <section className="relative h-[70vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black z-10" />
        <img
          src="https://images.unsplash.com/photo-1519662978799-2f05096d3636?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzY4NjMyMDUyfDA&ixlib=rb-4.1.0&q=85"
          alt="Contact"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="relative z-20 text-center px-6 max-w-4xl mx-auto">
          <p className="text-xs uppercase tracking-[0.3em] text-white/50 mb-6 font-light animate-fade-in-up">
            {language === 'en' ? 'GET IN TOUCH' : language === 'ar' ? 'تواصل معنا' : 'پەیوەندیمان پێوە بکە'}
          </p>
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white tracking-tight mb-6 animate-fade-in-up delay-200">
            {t('contact.title')}
          </h1>
          <p className="text-lg text-white/70 font-light animate-fade-in-up delay-300">{t('contact.subtitle')}</p>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-24 bg-black">
        <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
            {/* Contact Form */}
            <div className="scroll-reveal-left">
              <p className="text-xs uppercase tracking-[0.3em] text-white/40 mb-4 font-light">
                {language === 'en' ? 'SEND A MESSAGE' : language === 'ar' ? 'أرسل رسالة' : 'نامەیەک بنێرە'}
              </p>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-10 tracking-tight">
                {language === 'en' ? 'Send Us a Message' : language === 'ar' ? 'أرسل لنا رسالة' : 'نامەیەک بنێرە بۆمان'}
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <input
                  type="text"
                  placeholder={t('contact.form.name')}
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-6 py-4 bg-transparent border border-white/20 text-white placeholder-white/40 focus:border-white focus:outline-none transition-colors font-light"
                  required
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <input
                    type="tel"
                    placeholder={t('contact.form.phone')}
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-6 py-4 bg-transparent border border-white/20 text-white placeholder-white/40 focus:border-white focus:outline-none transition-colors font-light"
                    required
                  />
                  <input
                    type="email"
                    placeholder={t('contact.form.email')}
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-6 py-4 bg-transparent border border-white/20 text-white placeholder-white/40 focus:border-white focus:outline-none transition-colors font-light"
                    required
                  />
                </div>
                <select
                  value={formData.project}
                  onChange={(e) => setFormData({ ...formData, project: e.target.value })}
                  className="w-full px-6 py-4 bg-black border border-white/20 text-white focus:border-white focus:outline-none transition-colors font-light"
                >
                  <option value="" className="bg-black">{t('contact.form.project')}</option>
                  <option value="dli_shar" className="bg-black">Dli Shar</option>
                  <option value="bakrajo_hills" className="bg-black">Bakrajo Hills</option>
                  <option value="dina_city" className="bg-black">Dina City</option>
                  <option value="other" className="bg-black">Other</option>
                </select>
                <textarea
                  placeholder={t('contact.form.message')}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  rows={5}
                  className="w-full px-6 py-4 bg-transparent border border-white/20 text-white placeholder-white/40 focus:border-white focus:outline-none transition-colors resize-none font-light"
                />
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-luxury w-full px-8 py-5 bg-white text-black text-xs uppercase tracking-[0.15em] font-medium hover:bg-white/90 flex items-center justify-center gap-3 disabled:opacity-50"
                >
                  <span>{isSubmitting 
                    ? (language === 'en' ? 'Sending...' : language === 'ar' ? 'جاري الإرسال...' : 'ناردن...') 
                    : t('contact.form.submit')}</span>
                  <Send className="w-4 h-4" />
                </button>
              </form>
            </div>

            {/* Contact Information */}
            <div className="scroll-reveal-right space-y-12">
              {/* Map */}
              <div className="aspect-video overflow-hidden border border-white/10">
                <iframe
                  src={mapUrl}
                  width="100%"
                  height="100%"
                  style={{ border: 0, filter: 'grayscale(100%) invert(90%)' }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Darwazay Garmyan Location"
                />
              </div>

              {/* Contact Details */}
              <div className="space-y-8">
                <div className="flex items-start gap-6">
                  <div className="w-12 h-12 border border-white/20 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-white/50" />
                  </div>
                  <div>
                    <h4 className="text-xs uppercase tracking-[0.2em] text-white/40 mb-3 font-light">Address</h4>
                    <p className="text-white/70 font-light leading-relaxed">
                      {companyInfo.address[language] || companyInfo.address.en}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-6">
                  <div className="w-12 h-12 border border-white/20 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-white/50" />
                  </div>
                  <div>
                    <h4 className="text-xs uppercase tracking-[0.2em] text-white/40 mb-3 font-light">Phone</h4>
                    <div className="space-y-2">
                      {companyInfo.phone.slice(0, 2).map((phone) => (
                        <a
                          key={phone}
                          href={`tel:${phone.replace(/\s/g, '')}`}
                          className="block text-white/70 hover:text-white transition-colors font-light"
                        >
                          {phone}
                        </a>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-6">
                  <div className="w-12 h-12 border border-white/20 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-white/50" />
                  </div>
                  <div>
                    <h4 className="text-xs uppercase tracking-[0.2em] text-white/40 mb-3 font-light">Email</h4>
                    <a
                      href={`mailto:${companyInfo.email}`}
                      className="text-white/70 hover:text-white transition-colors font-light"
                    >
                      {companyInfo.email}
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-24 bg-white text-black">
        <div className="container mx-auto px-6 lg:px-12 max-w-4xl text-center">
          <p className="text-xs uppercase tracking-[0.3em] text-black/40 mb-4 font-light scroll-reveal">
            {language === 'en' ? 'STAY CONNECTED' : language === 'ar' ? 'ابق على تواصل' : 'پەیوەندی بپارێزە'}
          </p>
          <h2 className="text-3xl sm:text-4xl font-bold mb-6 tracking-tight scroll-reveal">
            {language === 'en' ? 'Stay Updated' : language === 'ar' ? 'ابق على اطلاع' : 'نوێکراوە بمێنەوە'}
          </h2>
          <p className="text-black/60 mb-10 font-light scroll-reveal">
            {language === 'en' ? 'Subscribe to our newsletter for the latest projects and updates' : language === 'ar' ? 'اشترك في نشرتنا الإخبارية لأحدث المشاريع والتحديثات' : 'بەشداربە لە نوسراوەکەمان بۆ نوێترین پڕۆژەکان و نوێکردنەوەکان'}
          </p>
          <form onSubmit={handleNewsletterSubmit} className="max-w-xl mx-auto scroll-reveal">
            <div className="flex flex-col sm:flex-row gap-4">
              <input
                type="email"
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                placeholder={language === 'en' ? 'Enter your email' : language === 'ar' ? 'أدخل بريدك الإلكتروني' : 'ئیمەیڵەکەت بنووسە'}
                className="flex-1 px-6 py-4 bg-transparent border border-black/20 text-black placeholder-black/40 focus:border-black focus:outline-none transition-colors font-light"
                required
              />
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-luxury px-10 py-4 bg-black text-white text-xs uppercase tracking-[0.15em] font-medium hover:bg-black/90 flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {isSubmitting 
                  ? (language === 'en' ? 'Subscribing...' : language === 'ar' ? 'جاري الاشتراك...' : 'بەشداربوون...') 
                  : (language === 'en' ? 'Subscribe' : language === 'ar' ? 'اشترك' : 'بەشداربە')}
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
